// "Send OTE questionnaire" (Quotations → OTE). Makes a one-time questionnaire link and opens the email in Outlook,
// as the bizSAFE enquiry form does. Picking an existing client pre-fills its company details and last year's
// endorsed activities on the form; the link stays tied to that client (never re-matched by name).
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { oteQuestionnaireEmail } from '../lib/oteQuote'
import { failed, toast } from '../lib/toast'

const newToken = () => (window.crypto?.randomUUID ? window.crypto.randomUUID().replace(/-/g, '') : Math.random().toString(36).slice(2) + Date.now().toString(36))

export default function SendOTEQuestionnaire({ onClose }) {
  const { profile } = useAuth()
  const [clients, setClients] = useState([])
  const [clientId, setClientId] = useState('')
  const [search, setSearch] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [link, setLink] = useState('')

  useEffect(() => {
    supabase.from('clients').select('id, name, contact_name, contact_email').order('name').then(({ data }) => setClients(data || []))
  }, [])

  const matches = search.trim()
    ? clients.filter(c => (c.name || '').toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8) : []
  const picked = clients.find(c => c.id === clientId)

  function pick(c) {
    setClientId(c.id); setSearch('')
    if (!name) setName(c.contact_name || '')
    if (!email) setEmail(c.contact_email || '')
  }

  async function send() {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { toast('Enter the client’s email', 'warning'); return }
    setSending(true)
    const token = newToken()
    const { data: me } = await supabase.auth.getUser()
    const { error } = await supabase.from('ote_form_links').insert({
      token, client_id: clientId || null, sent_to: email.trim(), initials: profile?.initials || null,
      created_by: me?.user?.email || null,   // the office notice on submit goes to this login email
    })
    if (error) { failed("Couldn't create the questionnaire link", error.message); setSending(false); return }
    const url = `${window.location.origin}/ote-form/${token}`
    const tmpl = oteQuestionnaireEmail({ contact: name.trim(), link: url })
    window.open(`mailto:${email.trim()}?subject=${encodeURIComponent(tmpl.subject)}&body=${encodeURIComponent(tmpl.body)}`)
    setLink(url)
    setSending(false)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Send OTE questionnaire</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
        </div>
        {link ? (
          <div>
            <div className="notice notice-info">Email opened in Outlook. Check it and click Send. Share this link if needed:</div>
            <div style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: 12, fontSize: 13, wordBreak: 'break-all', marginBottom: 12 }}>
              <span style={{ color: 'var(--accent)' }}>{link}</span>
            </div>
            <button className="sm" onClick={() => navigator.clipboard.writeText(link)}>Copy link</button>
            <div className="modal-footer"><button className="primary" onClick={onClose}>Done</button></div>
          </div>
        ) : (
          <>
            <div className="field">
              <label>Existing client? (optional)</label>
              {picked ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                  <strong>{picked.name}</strong>
                  <button className="sm" onClick={() => setClientId('')}>Change</button>
                </div>
              ) : (
                <>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Type to search clients — leave blank for a new company" />
                  {matches.length > 0 && (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4, maxHeight: 220, overflowY: 'auto' }}>
                      {matches.map(c => (
                        <div key={c.id} onClick={() => pick(c)} style={{ padding: '7px 10px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>{c.name}</div>
                      ))}
                    </div>
                  )}
                </>
              )}
              <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
                {picked ? 'The form will show this client’s company details and last year’s OTE activities (if any).' : 'New company: the client fills in everything.'}
              </div>
            </div>
            <div className="field"><label>Contact name (for the email)</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Mr Othman" /></div>
            <div className="field"><label>Client email *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contact@company.com" /></div>
            <div className="modal-footer">
              <button onClick={onClose}>Cancel</button>
              <button className="primary" onClick={send} disabled={sending}>{sending ? 'Preparing…' : 'Open email in Outlook'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
